import * as React from 'react';
import { Column, Icon, ResponsivePaginatedTable, Tooltip, createTableCell } from '@gcl/react-core';
import './UserTable.scss';
import { IProjectRole } from '@role/projectRole/projectRole.models';
import { IProjectTeam } from '@projectTeam/projectTeam.models';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import { ALL_USERS_TEAM_NAME } from '@common/constants';
import { createTableColumn } from '@common/tableColumn.services';
import { TTeamDisplayMode } from '../userList.constants';
import { UserCell } from './UserCell';

export interface IUserTableProps {
  teams?: IProjectTeam[] | undefined;
  roles: IProjectRole[] | undefined;
  openTeamModal?: (e: any) => void;
  openMemberModal: (e: any, userProfile?: IProjectUserProfile) => void;
  hasEditRight?: boolean;
  teamDisplayMode: TTeamDisplayMode;
  renderRoleHeader?: (role: IProjectRole) => React.ReactElement;
  setSelectedTeam?: React.Dispatch<React.SetStateAction<IProjectTeam | undefined>>;
  openConfirmDeleteTeamPortal?: (e: any) => void;
  setSelectedCompanyForForm?: () => void;
  colNum?: number;
}

export const UserTable: React.FunctionComponent<IUserTableProps> = ({
  teams,
  roles,
  openTeamModal,

  openMemberModal,
  hasEditRight = false,
  renderRoleHeader,
  setSelectedTeam,
  openConfirmDeleteTeamPortal,
  setSelectedCompanyForForm,
  teamDisplayMode,
  colNum,
}) => {
  const TableColumns: Column[] = React.useMemo(() => {
    const defaultColumns: Column[] = [];
    if (teamDisplayMode !== 'projectRequect' && teamDisplayMode !== 'companyGlobal')
      defaultColumns.push(
        createTableColumn({
          accessor: 'teamName',
          header: ' ',
          disableSortBy: true,
          disableResizing: true,
          cellRenderer: createTableCell<any, IProjectTeam>((value, item, index, cellExtras) => {
            if (!item.id || item.id <= 0) {
              if (cellExtras.hasEditRight)
                return (
                  <Tooltip overlay="Add Team" id="gcl-gems-user-table-add-team-btn-tooltip">
                    <Icon
                      iconName="PlusLg"
                      onClick={(e) => {
                        if (setSelectedCompanyForForm) setSelectedCompanyForForm();
                        if (setSelectedTeam) setSelectedTeam(undefined);
                        if (openTeamModal) openTeamModal(e);
                      }}
                      className="gcl-ic-btn-md"
                    />
                  </Tooltip>
                );
              return <></>;
            }
            return (
              <div className="gcl-gems-user-table-role-name-column-cell">
                <span className="gcl-gems-user-table-role-name-column-cell-role-name">{value}</span>
                {cellExtras.hasEditRight ? (
                  <>
                    <Tooltip
                      overlay={`Edit ${item.teamName}`}
                      id={`gcl-gems-user-table-edit-team-${item.id}-btn-tooltip`}
                    >
                      <Icon
                        data-testid={`edit-${item.teamName}`}
                        iconName="Pencil"
                        onClick={(e) => {
                          if (setSelectedCompanyForForm) setSelectedCompanyForForm();
                          if (setSelectedTeam) setSelectedTeam(item);
                          if (openTeamModal) openTeamModal(e);
                        }}
                        className="gcl-gems-user-table-icon-btn"
                      />
                    </Tooltip>
                    {item.teamName !== ALL_USERS_TEAM_NAME ? (
                      <Tooltip
                        mouseLeaveDelay={0}
                        overlay={`Delete ${item.teamName}`}
                        id={`gcl-gems-user-table-delete-team-${item.id}-btn-tooltip`}
                      >
                        <Icon
                          data-testid={`delete-${item.teamName}`}
                          iconName="Trash"
                          onClick={(e) => {
                            if (setSelectedTeam) setSelectedTeam(item);
                            if (openConfirmDeleteTeamPortal) openConfirmDeleteTeamPortal(e);
                          }}
                          className="gcl-gems-user-table-icon-btn"
                        />
                      </Tooltip>
                    ) : undefined}
                  </>
                ) : undefined}
              </div>
            );
          }),
        }),
      );

    const roleColumns =
      !roles || roles.length <= 0
        ? []
        : [
            ...roles.map((role: IProjectRole, colInex: number) => {
              return createTableColumn({
                accessor: `role column ${colInex}`,
                header: renderRoleHeader
                  ? () => renderRoleHeader(role)
                  : () => <span>{role.role?.name ?? role.name}</span>,
                disableSortBy: true,
                disableResizing: true,
                cellRenderer: createTableCell<any, IProjectTeam>((value, item, index, cellExtras) => {
                  return (
                    <UserCell
                      isShowTeam={teamDisplayMode !== 'projectRequect' && teamDisplayMode !== 'companyGlobal'}
                      team={item}
                      role={role}
                      hasEditRight={!!cellExtras.hasEditRight}
                      editUserProfile={(userProfile, e) => {
                        openMemberModal(e, userProfile);
                      }}
                      setSelectedCompanyForForm={setSelectedCompanyForForm}
                    />
                  );
                }),
              });
            }),
          ];

    let blankColumns: Column[] = [];
    if (colNum && colNum - roleColumns.length > 0) {
      blankColumns = [...Array(colNum - roleColumns.length)].map((s) => {
        return createTableColumn({
          header: ' ',
          accessor: 'id1',
          disableSortBy: true,
          disableResizing: true,
          cellRenderer: createTableCell(() => {
            return <></>;
          }),
        });
      });
    }
    return [...defaultColumns, ...roleColumns, ...blankColumns];
  }, [JSON.stringify(roles), teamDisplayMode, colNum]);

  const teamsLocal = React.useMemo(() => {
    if (teamDisplayMode === 'company') return teams;
    if (teamDisplayMode === 'allUsers') {
      const allUsers = teams?.find((s) => s.teamName === 'All Users');
      return allUsers ? [allUsers] : [];
    }
    return [];
  }, [JSON.stringify(teams), teamDisplayMode]);
  return (
    <>
      <div className={`gems-user-table ${teamDisplayMode === 'allUsers' ? 'gems-user-table-all-users' : ''}`}>
        <ResponsivePaginatedTable
          cellExtras={{ hasEditRight }}
          columns={TableColumns}
          disablePagination
          allowSelectRow={false}
          items={[
            ...(teamsLocal || []),
            ...(teamDisplayMode === 'allUsers'
              ? []
              : [
                  {
                    id: -1,
                    projectId: -1,
                    companyId: -1,
                    teamName: 'new team',
                  },
                ]),
          ]}
          allowExpandRow={false}
        />
      </div>
    </>
  );
};
